/**
 * The guest pointer — the ONLY guest state kept in browser storage (owner
 * decision 2026-10-07; contract "Browser pointer").
 *
 *   localStorage["garden.guest.v1"]        {v, threadId, allowance, claimIntent, hasSession, retentionDays?, updatedAt}
 *   sessionStorage["garden.guest.draft.v1"] the unsent composer text
 *
 * NO chat text in the pointer: the conversation lives on the server and comes
 * back through the `garden_gst` cookie (`?t=` + GET /chat/threads/{id}). No
 * token either (REQ-PRD-§11.2-A) — the guest JWT stays in auth-store memory.
 * The draft goes to sessionStorage, which survives the same-tab Google/email
 * round trip but not the tab closing, and is cleared once restored.
 *
 * Every Storage access is wrapped: private mode, a full quota or a disabled
 * site-data setting must degrade to "no pointer", never throw into a render.
 * TTL = the retention the pointer was written under, measured from the last
 * write: every write stamps the configured `retention_days`
 * (`setGuestPointerRetentionDays`, fed from GET /auth/guest/config) once it
 * is known, and a read uses that stamp — the boot refresh reads the pointer
 * BEFORE the config answers (final web fix round, item 9). An unstamped
 * pointer falls back to the configured value, then the contract default.
 */
import { useSyncExternalStore } from "react";

import { parseGuestAllowance, type GuestAllowance } from "@/features/chat/guest/allowance";

export const GUEST_POINTER_KEY = "garden.guest.v1";
export const GUEST_DRAFT_KEY = "garden.guest.draft.v1";
/** A sign-in started less than this long ago claims without asking. */
export const CLAIM_INTENT_MAX_AGE_MS = 30 * 60 * 1000;
/** Contract default for `guest.retention_days`. */
export const DEFAULT_GUEST_RETENTION_DAYS = 7;

const DAY_MS = 86_400_000;
/** Same-tab change signal (the `storage` event only reaches OTHER tabs). */
const POINTER_CHANGED_EVENT = "garden:guest-pointer";
/**
 * A pointer timestamped further ahead than this is clock skew at best and
 * tampering at worst — reject it like malformed JSON (hardening, fix round 1).
 */
export const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
/** Matches a UUID of any version/variant, case-insensitively. */
const UUID_SOURCE = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID_RE = new RegExp(`^${UUID_SOURCE}$`, "i");
/**
 * The only two shapes `guestReturnTo` can produce. The pointer is untrusted
 * (another tab, a browser extension, a user editing localStorage directly),
 * so a claim intent read back from storage is revalidated against this
 * before it is ever used as a navigation target (hardening, fix round 1).
 */
const RETURN_TO_RE = new RegExp(`^/chat(?:\\?t=${UUID_SOURCE})?$`, "i");

export interface GuestClaimIntent {
  /** Always `/chat?t=<thread>` or `/chat` (see `guestReturnTo`). */
  returnTo: string;
  /** Epoch ms when the in-app sign-in entry was used. */
  at: number;
}

export interface GuestPointer {
  v: 1;
  threadId: string | null;
  allowance: GuestAllowance | null;
  claimIntent: GuestClaimIntent | null;
  hasSession: boolean;
  /**
   * The retention, in days, this pointer was written under (final web fix
   * round, item 9) — its TTL on read, so a reload before the config answers
   * still measures it right. Stamped from the config on every write once the
   * config is known; carried over from the pointer itself before that, so
   * the boot refresh's own write never shortens it. Absent when no config was
   * ever known.
   */
  retentionDays?: number;
  updatedAt: number;
}

/**
 * A plausible retention: the setter and a pointer's own stamp accept the
 * same values (the server bounds `guest.retention_days` to 1–90; a year is a
 * generous ceiling for a value read back from untrusted storage).
 */
const MAX_RETENTION_DAYS = 365;

function isRetentionDays(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_RETENTION_DAYS
  );
}

/** GET /auth/guest/config's `retention_days` once it answered; null before. */
let configuredRetentionDays: number | null = null;

/** Fed from GET /auth/guest/config (`retention_days`). */
export function setGuestPointerRetentionDays(days: number): void {
  if (isRetentionDays(days)) configuredRetentionDays = days;
}

/** Test seam: a fresh boot, before GET /auth/guest/config has answered. */
export function __resetGuestPointerRetentionForTests(): void {
  configuredRetentionDays = null;
}

/** A pointer's TTL in days: its own stamp, else the configured value, else the default. */
function ttlDays(stamp: number | undefined): number {
  return stamp ?? configuredRetentionDays ?? DEFAULT_GUEST_RETENTION_DAYS;
}

function store(kind: "local" | "session"): Storage | null {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function parseIntent(raw: unknown): GuestClaimIntent | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const returnTo = o["returnTo"];
  const at = o["at"];
  // Enforce the documented invariant on READ, not just on write: `returnTo`
  // must be exactly `/chat` or `/chat?t=<uuid>` (see `GuestClaimIntent`
  // above). Anything else — an absolute URL, a protocol-relative one, a
  // `javascript:` scheme, a `?t=` with a non-UUID payload — is dropped as
  // "no intent" rather than ever reaching a navigation call (hardening, fix
  // round 1).
  if (typeof returnTo !== "string" || !RETURN_TO_RE.test(returnTo)) return null;
  if (typeof at !== "number" || !Number.isFinite(at)) return null;
  return { returnTo, at };
}

/** Pure: no storage writes (safe inside `useSyncExternalStore`'s snapshot). */
function parsePointer(raw: string, now: number): GuestPointer | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const o = data as Record<string, unknown>;
  const updatedAt = o["updatedAt"];
  if (o["v"] !== 1 || typeof updatedAt !== "number" || !Number.isFinite(updatedAt)) return null;
  // A nonsensical stamp is ignored rather than trusted: the configured value
  // (or the default) applies, which can only ever shorten the TTL.
  const stampRaw = o["retentionDays"];
  const stamp = isRetentionDays(stampRaw) ? stampRaw : undefined;
  if (now - updatedAt > ttlDays(stamp) * DAY_MS) return null;
  // Stamped further in the future than plausible clock skew — malformed,
  // like bad JSON (hardening, fix round 1).
  if (updatedAt - now > MAX_CLOCK_SKEW_MS) return null;
  const threadIdRaw = o["threadId"];
  let threadId: string | null;
  if (threadIdRaw === null || threadIdRaw === undefined) {
    threadId = null;
  } else if (typeof threadIdRaw === "string" && UUID_RE.test(threadIdRaw)) {
    threadId = threadIdRaw;
  } else {
    // Present but not a UUID — treat the whole pointer as malformed rather
    // than silently dropping just this field (hardening, fix round 1).
    return null;
  }
  return {
    v: 1,
    threadId,
    allowance: parseGuestAllowance(o["allowance"]),
    claimIntent: parseIntent(o["claimIntent"]),
    hasSession: o["hasSession"] === true,
    ...(stamp !== undefined ? { retentionDays: stamp } : {}),
    updatedAt,
  };
}

function readRaw(): string | null {
  const local = store("local");
  if (local === null) return null;
  try {
    return local.getItem(GUEST_POINTER_KEY);
  } catch {
    return null;
  }
}

function notifyChanged(): void {
  try {
    window.dispatchEvent(new Event(POINTER_CHANGED_EVENT));
  } catch {
    // No window (never in the browser) — nothing to notify.
  }
}

export function emptyGuestPointer(now: number = Date.now()): GuestPointer {
  return {
    v: 1,
    threadId: null,
    allowance: null,
    claimIntent: null,
    hasSession: false,
    updatedAt: now,
  };
}

/** The pointer, or null; a malformed or expired one is removed. */
export function readGuestPointer(now: number = Date.now()): GuestPointer | null {
  const raw = readRaw();
  if (raw === null) return null;
  const pointer = parsePointer(raw, now);
  if (pointer === null) clearGuestPointer();
  return pointer;
}

export function writeGuestPointer(pointer: GuestPointer, now: number = Date.now()): void {
  const local = store("local");
  if (local === null) return;
  // Explicit field list — never a blind `{...pointer}` spread — so a caller
  // holding a pointer-shaped variable with an extra field (e.g. `lastMessage`,
  // `transcript`) can never persist chat text here, which is the hard
  // invariant at the top of this file (hardening, fix round 1). `allowance`
  // and `claimIntent` are rebuilt through their own parsers rather than
  // copied, for the same reason: a parser only ever emits the known shape.
  // `retentionDays` (item 9): the configured value when known, else the
  // pointer's own valid stamp — a write before the config answers (the boot
  // refresh) must not drop or shorten it.
  const stamp =
    configuredRetentionDays ??
    (isRetentionDays(pointer.retentionDays) ? pointer.retentionDays : null);
  const next: GuestPointer = {
    v: 1,
    // Symmetric with the read side (`parsePointer` above): a non-UUID
    // threadId never reaches storage (W2.1 re-review carry).
    threadId: pointer.threadId !== null && UUID_RE.test(pointer.threadId) ? pointer.threadId : null,
    allowance: parseGuestAllowance(pointer.allowance),
    claimIntent: parseIntent(pointer.claimIntent),
    hasSession: pointer.hasSession === true,
    ...(stamp !== null ? { retentionDays: stamp } : {}),
    updatedAt: now,
  };
  try {
    local.setItem(GUEST_POINTER_KEY, JSON.stringify(next));
  } catch {
    return;
  }
  notifyChanged();
}

/** Read-modify-write; creates the pointer when absent. */
export function updateGuestPointer(
  patch: Partial<Omit<GuestPointer, "v" | "updatedAt">>,
  now: number = Date.now(),
): GuestPointer {
  const next: GuestPointer = {
    ...(readGuestPointer(now) ?? emptyGuestPointer(now)),
    ...patch,
    v: 1,
    updatedAt: now,
  };
  writeGuestPointer(next, now);
  return next;
}

export function clearGuestPointer(): void {
  const local = store("local");
  if (local === null) return;
  try {
    local.removeItem(GUEST_POINTER_KEY);
  } catch {
    return;
  }
  notifyChanged();
}

export function readGuestDraft(): string | null {
  const session = store("session");
  if (session === null) return null;
  try {
    const value = session.getItem(GUEST_DRAFT_KEY);
    return value !== null && value.trim().length > 0 ? value : null;
  } catch {
    return null;
  }
}

/** An empty or whitespace-only draft removes the key. */
export function writeGuestDraft(text: string): void {
  const session = store("session");
  if (session === null) return;
  try {
    if (text.trim().length === 0) session.removeItem(GUEST_DRAFT_KEY);
    else session.setItem(GUEST_DRAFT_KEY, text);
  } catch {
    // Quota / disabled storage: losing a draft is better than throwing.
  }
}

export function clearGuestDraft(): void {
  writeGuestDraft("");
}

export function isClaimIntentFresh(
  intent: GuestClaimIntent | null,
  now: number = Date.now(),
): boolean {
  if (intent === null) return false;
  const age = now - intent.at;
  return age >= 0 && age < CLAIM_INTENT_MAX_AGE_MS;
}

/** Where every guest sign-in entry returns to (contract: `/chat?t=<id>`). */
export function guestReturnTo(pointer: GuestPointer | null): string {
  return pointer !== null && pointer.threadId !== null
    ? "/chat?t=" + encodeURIComponent(pointer.threadId)
    : "/chat";
}

/**
 * Record that an in-app sign-in entry was used (limit sheet, locked feature,
 * header «Войти», «Ещё»). Only meaningful while a guest session exists;
 * returns the `return_to` to put on the link either way.
 */
export function recordClaimIntent(now: number = Date.now()): string {
  const pointer = readGuestPointer(now);
  const returnTo = guestReturnTo(pointer);
  if (pointer !== null && pointer.hasSession) {
    writeGuestPointer({ ...pointer, claimIntent: { returnTo, at: now } }, now);
  }
  return returnTo;
}

let sessionExpired = false;

/**
 * api-client notes a guest session the server ended: a guest refresh's 401
 * only (contract amendment B-5). A 503 `guest.unavailable` (Redis down) is
 * "unavailable" and keeps the session (B-3) — it never calls this.
 */
export function noteGuestSessionExpired(): void {
  sessionExpired = true;
}

/** ChatRoute shows "the earlier chat expired" once. */
export function takeGuestSessionExpired(): boolean {
  const was = sessionExpired;
  sessionExpired = false;
  return was;
}

let cachedRaw: string | null | undefined;
let cachedPointer: GuestPointer | null = null;

/**
 * `useSyncExternalStore`'s snapshot. Must stay side-effect-free — no storage
 * write/remove here, React may call this during a render that gets thrown
 * away — and must return the SAME object reference across calls while
 * nothing has changed, or React logs an infinite-loop warning. The cache
 * above covers that; what it cannot cover by itself is TTL expiry reached
 * purely by the passage of time with no storage write in between, so that is
 * re-checked against `Date.now()` on every call, independent of the cache
 * (hardening, fix round 1). The re-check only ever narrows a cached pointer
 * to `null`, which is trivially referentially stable.
 */
function getSnapshot(): GuestPointer | null {
  const raw = readRaw();
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedPointer = raw === null ? null : parsePointer(raw, Date.now());
  }
  if (
    cachedPointer !== null &&
    Date.now() - cachedPointer.updatedAt > ttlDays(cachedPointer.retentionDays) * DAY_MS
  ) {
    return null;
  }
  return cachedPointer;
}

export function subscribeGuestPointer(listener: () => void): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key === GUEST_POINTER_KEY) listener();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(POINTER_CHANGED_EVENT, listener);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(POINTER_CHANGED_EVENT, listener);
  };
}

/** The pointer as React state: re-renders on this tab's writes and other tabs'. */
export function useGuestPointer(): GuestPointer | null {
  return useSyncExternalStore(subscribeGuestPointer, getSnapshot, () => null);
}
